import { Box, TypographyStylesProvider } from "@mantine/core";
import React from "react";
import { IconBoltFilled } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";

export const LIGHTNING_SENT_CONTENT = (
  <TypographyStylesProvider>
    <p>
      In the history menu, we can see detailed data about outbound lightning
      transactions. This information helps you understand how payments are
      routed, the associated fees, and other specifics about your node's
      interactions with the Lightning Network.
    </p>
    <DocsButton
      ariaLabel="lightning Sent button"
      text="Lightning Sent"
      leftSection={<IconBoltFilled />}
    />
    <p>You will see something like this:</p>
    <Box mb="20px">
      <JsonView
        data={{
          kind: "pay-offchain-event",
          event: {
            id: "da7c365f6efcd19178a624868e6049a1b6381fe545c34a0e3cdff22fa26ccb57",
            fee: 1,
            hops: [
              {
                fee: 1,
                channel: "16000000x0x0",
                forward: 1,
                timeout: 869046,
                public_key:
                  "02ccdfc63e16c64f415fa9ba8d8c8dfabb532b73455c031f2c63ce14eb06f08627",
                fee_mtokens: "1000",
                forward_mtokens: "1000",
                channel_capacity: 500000,
              },
              {
                fee: 0,
                channel: "867550x991x0",
                forward: 1,
                timeout: 869046,
                public_key:
                  "0322d0e43b3d92d30ed187f4e101a9a9605c3ee5fc9721e6dac3ce3d7732fbb13e",
                fee_mtokens: "0",
                forward_mtokens: "1000",
                channel_capacity: 5000000,
              },
            ],
            index: "1025",
            secret:
              "c22fffd1e0a37ff126e6614724f24bdc18e73e19110844fe18f286a663db5ca4",
            tokens: 2,
            mtokens: "2000",
            request:
              "lnbc10n1pnjj5ncpp5mf7rvhmwlngez79xyjrguczf5xmrs8l9ghp55r3umlezlgnvedtsdqqcqzysxqyz5vqsp507rk35p709g3dv6ydqe6yhzgtamwvp99t0q4qjpg56r6thl3y9vq9p4gqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqpqysgq433z2x0ljawwhfsp5v8t5uhg867emc56g9re0n5zknq073pqtdhkhwzuxjgafdf3847lppv0yvuttccejjn49spnk6tm3apfxr6sxvsqpplxua",
            timeout: 869126,
            safe_fee: 1,
            created_at: "2024-11-04T23:02:38.808Z",
            destination:
              "0322d0e43b3d92d30ed187f4e101a9a9605c3ee5fc9721e6dac3ce3d7732fbb13e",
            fee_mtokens: "1000",
            safe_tokens: 2,
            confirmed_at: "2024-11-04T23:02:39.347Z",
          },
          status: "success",
          eventKind: "confirmed",
          eventParent: "pay-via-request",
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>
    <p>
      Looking in detail at this data can teach us a lot about how the lightning
      network works:
    </p>
    <ul>
      <li>
        <b>id:</b> Unique identifier for the payment event.
      </li>
      <li>
        <b>fee:</b> Total fee in satoshis for routing this transaction. This is
        the sum of fees collected by the intermediary hops.
      </li>
      <li>
        <b>hops:</b> Details about each node/channel the payment traversed. Each
        hop provides:
        <ul>
          <li>
            <b>channel:</b> Identifier of the channel used for this hop.
          </li>
          <li>
            <b>fee:</b> Fee paid to this hop for routing the payment.
          </li>
          <li>
            <b>forward:</b> Amount forwarded to the next hop.
          </li>
          <li>
            <b>public_key:</b> Public key of the node managing this hop.
          </li>
        </ul>
      </li>
      <li>
        <b>index:</b> A strictly increasing number assigned to each payment on
        the node. This ensures uniqueness and provides a reliable ordering of
        payments over time.
      </li>
      <li>
        <b>secret:</b> The preimage of the payment hash. This is a cryptographic
        value known only to the recipient initially. Once the recipient provides
        this preimage to the sender, the payment is considered settled.
      </li>
      <li>
        <b>tokens:</b> The amount sent in satoshis (1 sat = 0.00000001 BTC).
      </li>
      <li>
        <b>mtokens:</b> Millisatoshis (1 mtokens = 0.001 sats), which provide
        greater precision for routing fees and smaller transactions. This is one
        of the curious design choices of the Lightning Network, and it's led to
        a good deal of problems, with developers often mixing up satoshis and
        millisatoshis, sometimes with disastrous results.
      </li>
      <li>
        <b>request:</b> The Bolt11 payment request ("invoice") used to initiate
        the transaction. This encodes details like the destination, amount, and
        expiration.
      </li>
      <li>
        <b>timeout:</b> The block height at which the payment expires. In the
        Lightning Network, this ensures that funds cannot be locked indefinitely
        in a transaction. If the recipient doesn’t claim the payment before this
        block height, the sender can reclaim the funds.
      </li>

      <li>
        <b>created_at:</b> A timestamp in ISO format indicating when the payment
        was initiated.
        <ul>
          <li>
            This timestamp is a <b>Unix timestamp</b>, which counts the number
            of seconds since January 1, 1970 (the Unix epoch). It’s a common way
            of representing time in software systems.
          </li>
        </ul>
      </li>
      <li>
        <b>destination:</b> The public key of the node that received the
        payment. This is used for identifying the recipient on the lightning
        network.
      </li>
      <li>
        <b>confirmed_at:</b> A timestamp indicating when the payment was
        confirmed.
      </li>
      <li>
        <b>status:</b> Indicates the outcome of the payment (e.g., "success").
      </li>
    </ul>
  </TypographyStylesProvider>
);
